<template>
    <div class="comment-thread" :class="{'is-empty': !hasVisibleContent}">
        <div
            class="comment"
            v-for="comment in displayComments"
            :key="comment.id"
            :class="{'is-hidden-comment': comment.hidden}"
        >
            <div class="avatar" v-if="comment.author_photo_file">
                <img :src="apiUrl + 'app/user/photo/' + comment.author_photo_file"/>
            </div>
            <!-- Roger-authored replies surface as the company, not a person -->
            <div class="avatar is-company" v-else-if="!comment.user_id">
                <i class="fas fa-tennis-ball"></i>
            </div>
            <div class="avatar" v-else>
                <i class="fas fa-user"></i>
            </div>
            <div class="bubble">
                <div class="meta">
                    <strong>{{ comment.author_name }}</strong>
                    <b-tag type="is-primary" size="is-small" class="ml-2" v-if="comment.is_staff_post">
                        <i class="fas fa-star mr-1"></i>Staff
                    </b-tag>
                    <span class="lock ml-2" v-if="comment.visibility === 'staff'" title="Only visible to staff">
                        <i class="fas fa-lock mr-1"></i>Staff only
                    </span>
                    <span class="is-size-7 has-text-grey ml-2">{{ comment.created_at | moment('from') }}</span>
                    <span class="lock ml-2" v-if="comment.hidden"><i class="fas fa-eye-slash mr-1"></i>Hidden</span>
                </div>
                <div class="body">{{ comment.body }}</div>
            </div>
            <b-dropdown position="is-bottom-left" aria-role="menu" v-if="isStaff || comment.user_id === currentUser.id">
                <template #trigger>
                    <b-button size="is-small" type="is-text"><i class="fas fa-ellipsis-h"></i></b-button>
                </template>
                <b-dropdown-item aria-role="menuitem" v-if="isStaff && !comment.hidden" @click="hide(comment, 1)">
                    <i class="fas fa-eye-slash fa-fw mr-2"></i>Hide
                </b-dropdown-item>
                <b-dropdown-item aria-role="menuitem" v-if="isStaff && comment.hidden" @click="hide(comment, 0)">
                    <i class="fas fa-eye fa-fw mr-2"></i>Unhide
                </b-dropdown-item>
                <b-dropdown-item aria-role="menuitem" class="has-text-danger" @click="remove(comment)">
                    <i class="fas fa-trash fa-fw mr-2"></i>Delete
                </b-dropdown-item>
            </b-dropdown>
        </div>

        <div class="mt-2 mb-2" v-if="loading">
            <span class="has-text-grey is-size-7"><i class="fal fa-tennis-ball fa-spin mr-2"></i>Loading replies...</span>
        </div>

        <!-- Wall posts auto-expand their first three; announcements stay
             collapsed until asked. Either way the rest are one click away. -->
        <a class="is-size-7 show-all" v-if="showViewAll" @click="expand">
            <i class="far fa-comments mr-1"></i>{{ previewShown ? 'View all' : 'View' }} {{ post.comment_count }} {{ post.channel === 'announcement' ? 'replies' : 'comments' }}
        </a>

        <div class="composer mt-3" v-if="composerOpen">
            <b-input
                ref="input"
                v-model="newComment"
                type="textarea"
                rows="2"
                :placeholder="post.channel === 'announcement' ? 'Write a reply...' : 'Write a comment...'"
                custom-class="comment-input"
            />
            <div class="columns is-mobile is-gapless mt-2">
                <div class="column">
                    <!-- Reply routing only exists on announcements: to everyone,
                         or privately to trip staff. -->
                    <b-field v-if="post.channel === 'announcement' && !isStaff">
                        <b-radio v-model="visibility" native-value="group" size="is-small">
                            <i class="far fa-users mr-1"></i>Reply to group
                        </b-radio>
                        <b-radio v-model="visibility" native-value="staff" size="is-small" class="ml-3">
                            <i class="far fa-lock mr-1"></i>Staff only
                        </b-radio>
                    </b-field>
                </div>
                <div class="column is-narrow">
                    <b-button type="is-primary" size="is-small" :disabled="!newComment.trim() || sending" @click="submit">
                        <i class="fas fa-paper-plane mr-2"></i>Send
                    </b-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from 'vuex';
    import {client as http, url} from '@/http_client';
    import * as socket from '@/socket';

    export default {
        name: 'CommentThread',
        props: {
            post: {type: Object, required: true},
            regId: {type: Number, required: true},
            isStaff: {type: Boolean, default: false}
        },
        data: function() {
            return {
                apiUrl: url,
                comments: [],
                expanded: false,
                loading: false,
                composerOpen: false,
                newComment: '',
                visibility: 'group',
                sending: false
            };
        },
        computed: {
            ...mapGetters(['currentUser']),
            // Wall posts show their embedded first-three preview by default;
            // announcements keep their thread collapsed until asked.
            previewShown: function() {
                return this.post.channel === 'wall';
            },
            displayComments: function() {
                if (this.expanded) return this.comments;
                return this.previewShown ? (this.post.comments || []) : [];
            },
            showViewAll: function() {
                if (this.expanded || this.loading) return false;
                return this.post.comment_count > this.displayComments.length;
            },
            hasVisibleContent: function() {
                return this.displayComments.length > 0 || this.showViewAll || this.composerOpen || this.loading;
            }
        },
        methods: {
            expand: function() {
                this.expanded = true;
                this.load();
            },
            load: function() {
                this.loading = true;
                http.get(`/app/experience/${this.regId}/posts/${this.post.id}/comments`, {force: true}).then(response => {
                    this.comments = response.data.comments;
                    this.loading = false;
                });
            },
            // The footer Reply/Comment action opens (or closes) the input.
            toggleComposer: function() {
                this.composerOpen = !this.composerOpen;
                if (this.composerOpen) {
                    this.$nextTick(() => {
                        if (this.$refs.input) this.$refs.input.focus();
                    });
                }
            },
            submit: function() {
                if (this.sending) return;
                this.sending = true;
                http.post(
                    `/app/experience/${this.regId}/posts/${this.post.id}/comments`,
                    {body: this.newComment, visibility: this.visibility},
                    {force: true}
                ).then(() => {
                    this.newComment = '';
                    this.sending = false;
                    if (this.expanded) this.load();
                    // Parent refetch refreshes the embedded preview + count.
                    this.$emit('changed');
                }).catch(() => {
                    this.sending = false;
                });
            },
            hide: function(comment, hidden) {
                http.put(
                    `/app/experience/${this.regId}/posts/${this.post.id}/comments/${comment.id}/hide`,
                    {hidden},
                    {force: true}
                ).then(() => {
                    if (this.expanded) this.load();
                    this.$emit('changed');
                });
            },
            remove: function(comment) {
                http.delete(
                    `/app/experience/${this.regId}/posts/${this.post.id}/comments/${comment.id}`,
                    {force: true}
                ).then(() => {
                    if (this.expanded) this.load();
                    this.$emit('changed');
                });
            },
            onCommentSignal: function(d) {
                if (d.postId === this.post.id && this.expanded) this.load();
            }
        },
        mounted() {
            socket.on('comment:changed', this.onCommentSignal);
        },
        beforeDestroy() {
            socket.off('comment:changed', this.onCommentSignal);
        }
    };
</script>

<style lang="scss" scoped>
    .comment-thread {
        border-top: 1px solid #f0f0f0;
        padding-top: 0.8rem;

        // Nothing to show and no composer open: take up no space at all
        // (announcements stay visually simple).
        &.is-empty {
            display: none;
        }

        .show-all {
            display: inline-block;
            font-weight: 600;
            margin-top: 0.2rem;
        }

        .comment {
            display: flex;
            align-items: flex-start;
            margin-bottom: 0.6rem;

            &.is-hidden-comment {
                opacity: 0.6;
            }

            .avatar {
                flex: 0 0 30px;
                width: 30px;
                height: 30px;
                border-radius: 50%;
                overflow: hidden;
                border: 1px solid #e7e7e7;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: #f5f5f5;
                margin-top: 0.2rem;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                i {
                    color: #bebebe;
                    font-size: 0.8rem;
                }

                &.is-company {
                    background-color: #628b21;

                    i {
                        color: white;
                    }
                }
            }

            .bubble {
                flex: 1;
                background-color: #f6f6f6;
                border-radius: 0.6rem;
                padding: 0.5rem 0.8rem;
                margin-left: 0.6rem;

                .meta {
                    font-size: 0.8rem;

                    .lock {
                        color: #b08b00;
                        font-size: 0.75rem;
                    }
                }

                .body {
                    white-space: pre-wrap;
                    word-break: break-word;
                    font-size: 0.9rem;
                    margin-top: 0.2rem;
                }
            }
        }
    }
</style>
